/**
 * THE PUBLIC MUSIC CATALOGUE, and the three views the Home page shows of it.
 *
 * ONE SOURCE OF TRUTH. `publicCatalogue()` decides which songs are eligible for
 * discovery at all; Featured, Trending and Explore are then three readings of that one
 * list. They deliberately overlap — a song can be featured AND trending AND in Explore,
 * because those are three ways of looking at the same catalogue rather than three
 * datasets. No section holds a list of its own, so there is no way for them to disagree
 * about what music exists, and no section re-implements the eligibility rule.
 *
 * TWO OF THE THREE ARE ORDERINGS; FEATURED IS A SHORTLIST. Trending and Explore contain the
 * whole catalogue (Trending minus the featured track), so being discoverable is enough to be
 * in them. Featured is the TOP of a ranking, capped — because if every published song were
 * featured, "featured" would be a synonym for "published" and the section would be Explore
 * with a different heading.
 *
 * WHAT THIS IS, AND WHAT IT REFUSES TO BE. The app has no follows and no recommendation
 * service, and the two counts `Song` does carry — plays and Loves (see lib/engagement) —
 * are local to this browser and this account. So the lists could either be invented, or
 * they could be a real reading of real data with the reading stated out loud. This is the
 * second, for the same reason lib/sections.ts derives section markers from the lyric sheet
 * instead of fabricating timestamps: an approximation the UI admits to is honest, and a
 * plausible-looking invention is not.
 *
 * ELIGIBILITY IS TWO CONDITIONS, NOT ONE: `status === 'ready'` AND
 * `visibility === 'public'`. Generation finishing is not permission to publish. That
 * distinction is the whole reason `isPubliclyDiscoverable` exists as a named function
 * rather than an inline filter — read its comment before changing anything here.
 *
 * WHICH MEANS THE CATALOGUE IS EMPTY UNTIL SOMEBODY LISTS A SONG, and that is the honest
 * outcome. The seed library and every generated track default to `'private'`, so a fresh
 * session's catalogue is empty and the Home page shows its empty state. The Library's "Make
 * discoverable" action is what fills it, one song at a time, on a real decision by the person
 * who owns the track. Relaxing the rule to fill the page would mean publishing songs nobody
 * chose to publish.
 *
 * This paragraph used to say the catalogue was empty because "there is no Publish action for a
 * user to take", and it was left standing after that action shipped. Corrected rather than
 * deleted: empty-by-default is still true and is still why the empty state exists.
 *
 * THE FOUR VIEWS, all of them over fields the app actually has:
 *
 *   catalogue  every publicly discoverable song, newest first. The set the other three
 *              draw from — and the only place eligibility is decided.
 *   featured   the catalogue's strongest few by `lib/engagement.featuredScore` — Loves,
 *              then plays, then saves — newest first on a tie, capped at FEATURED_MAX. A
 *              SHORTLIST, which is what distinguishes it from `catalogue`: being
 *              discoverable puts a song in Explore, not in Featured.
 *   trending   ranked by overall engagement so far, newest first on a tie. Listening and
 *              appreciation are different facts, so plays, Loves, saves and shares all count
 *              through lib/engagement.trendingScore; newer songs still break ties. Excludes
 *              the featured track so the same cover does not lead two sections in a row.
 *   explore    the catalogue under the active genre filter, newest first. Genres come
 *              from the catalogue itself (`catalogueGenres`), not from a hardcoded list,
 *              so a chip can never offer a genre no song has.
 *
 * WHAT IT IS NOT. Nothing here is cross-account, nothing here is personalised, and
 * "trending" does not mean trending among other people — it cannot, because this build
 * has no other people. api/discoveryApi.describeDiscoveryBlockers() carries that fact in
 * a form the screen renders, and the Home page renders it.
 *
 * Pure and deterministic: same library in, same lists out. The input array is never
 * mutated — every sort runs on a copy, because the caller's array is the songs store's
 * own state.
 */
import {
  featuredScore,
  featuredScoreFromCounts,
  trendingScore,
  trendingScoreFromCounts,
  type FeaturedSignalCounts,
  type SaveCounts,
} from './engagement';
import type { Song } from '../types/domain';

/**
 * The "no genre filter" sentinel.
 *
 * The same string songsStore's DEFAULT_FILTERS uses, deliberately: the Library's genre
 * select and the Home page's genre chips are two controls over one idea, and two
 * different sentinels for "everything" is how they drift apart. The chip renders it as
 * "All" — the label is presentation, the value is shared.
 */
export const ALL_GENRES = 'All genres';

/** The catalogue, and the views of it the Home page renders. */
export interface Showcase {
  /**
   * Every song eligible for discovery, newest first. THE source of truth — the other
   * lists are orderings of this one and never contain anything outside it.
   */
  catalogue: Song[];
  /** Genres present in the catalogue, sorted, for the Explore filter chips. */
  genres: string[];
  /** The genre `explore` was filtered by. ALL_GENRES when unfiltered. */
  genre: string;
  /** The one song given the hero treatment, or null when the catalogue is empty. */
  featured: Song | null;
  /**
   * The rest of the featured selection — the shortlist's runners-up, in ranked order.
   *
   * STILL RETURNED THOUGH THE HOME PAGE NO LONGER DRAWS IT. Featured is a single hero there now,
   * so nothing renders these today; they stay in the response because they are a real part of
   * the ranking rather than a rail's data source, and dropping them would make `featured` the
   * only thing the module could say about a shortlist it computes in full. See
   * routes/Dashboard.tsx, which reads `featured` and not this.
   */
  featuredRail: Song[];
  /** Ranked, never including `featured`. */
  trending: Song[];
  /** The catalogue under `genre`, newest first. */
  explore: Song[];
}

/** Options a caller can narrow the catalogue with. */
export interface ShowcaseOptions {
  /** A genre from `catalogueGenres`, or ALL_GENRES. Affects `explore` only. */
  genre?: string;
  /**
   * Saves per song id — playlists containing each song.
   *
   * Passed in rather than read, because this module is pure and saves live in the playlists
   * store; hooks/useShowcase is the one caller that holds both and builds it with
   * stores/playlistsStore.songSaveCounts. Omitted means no saves are known, which scores
   * every song's save signal at zero — see lib/engagement.SaveCounts.
   */
  saves?: SaveCounts;
  /** Signals used to pick Featured. Omitted means Featured uses the song totals. */
  featuredSignals?: Readonly<Record<string, FeaturedSignalCounts>>;
  /** Back-compat alias for callers/tests still naming the old override. */
  weeklyFeatured?: Readonly<Record<string, FeaturedSignalCounts>>;
  /** Signals used to rank Trending. Omitted means Trending uses the song totals. */
  trendingSignals?: Readonly<Record<string, FeaturedSignalCounts>>;
  /** A fixed Featured winner to hold for the whole day, when one was decided previously. */
  featuredId?: string | null;
}

/**
 * How many rows Trending draws — the one display limit the Home page still has.
 *
 * A DISPLAY limit, not a data limit: `trendingSongs` returns the whole ranking and this is how
 * much of it the page draws. It is A CAP WITH NOTHING BEHIND IT, which is the point — a chart's
 * job is to be the short list, so the section IS the top TRENDING_VISIBLE and there is
 * deliberately no control to unroll it into the catalogue. Three, so the chart reads as a podium
 * rather than as a list.
 *
 * DISPLAY-ONLY, and worth being explicit about: this number changes how many rows are drawn and
 * nothing about the order they are in. The arithmetic behind that order is
 * lib/engagement.trendingScore and it does not know this constant exists.
 *
 * THERE IS NO `FEATURED_VISIBLE` AND NO `EXPLORE_VISIBLE`, because neither section trims any
 * more. Featured draws the head of its selection and nothing else — it was a hero plus four
 * cards of the runners-up behind a "View all", which put a rail of covers Explore already holds
 * directly under the hero. Explore is a sideways-scrolling rail holding the whole catalogue, so
 * its height does not depend on how many songs it has and every card is already reachable; it
 * had a limit of 6 when it was a grid behind a "View all" that grew the page by a row of cards
 * for every few songs.
 *
 * Note what did NOT change with either removal: `featuredSongs` still returns the whole
 * shortlist and `Showcase.featuredRail` still carries the runners-up. The ranking is the data
 * layer's; how much of it a page draws is the page's.
 */
export const TRENDING_VISIBLE = 3;

/**
 * How many songs Featured may hold at most — a DATA limit, unlike TRENDING_VISIBLE above.
 *
 * THIS IS WHAT MAKES FEATURED A SHORTLIST. Every publicly discoverable song is in Explore;
 * Featured is the strongest few. Without a cap the two sections would hold the same songs in
 * a different order, and "featured" would mean nothing more than "published".
 *
 * Eight, so the shortlist is a selection rather than a single pick, and small enough that a
 * thousand-song catalogue does not turn it into a second Explore. UNCHANGED BY THE HOME PAGE
 * DRAWING ONLY THE HEAD OF IT: this is a fact about the ranking, and how much of a ranking a
 * page renders is not the ranking's business.
 */
export const FEATURED_MAX = 8;

/**
 * How many songs must have real engagement before Featured is built from them alone.
 *
 * BELOW THIS IT IS THE LOW-DATA FALLBACK: the best eligible public songs, still ranked by the
 * same score, rather than a shortlist assembled from the only one or two songs anyone has
 * touched. Three is the smallest number that makes "the catalogue's strongest tracks" a reading
 * of the catalogue rather than a reading of a coincidence — one Love in an untouched library
 * would otherwise decide the whole selection on its own.
 *
 * Left at three deliberately now that the Home page draws only the head of the shortlist. The
 * visible consequence is narrower than it was — it decides WHICH song is the hero rather than
 * the make-up of a rail — but the question it answers is unchanged: is there enough engagement
 * here to rank on, or not?
 */
export const FEATURED_MIN_ENGAGED = 3;

/** Newest first. `seq` is the songs store's monotonic creation counter. */
function bySeqDesc(a: Song, b: Song): number {
  return b.seq - a.seq;
}

/**
 * The ranking behind Trending: plays and Loves together, newest first on a tie.
 *
 * BOTH SIGNALS, WHICH IS THE REQUIREMENT. Listening and appreciation are different facts
 * about a song and Trending considers both — so this is neither "most plays" (which
 * rewards a mis-click as much as a favourite track) nor "most Loves" (which ignores
 * whether anybody actually listened). `lib/engagement.trendingScore` adds them, and it
 * adds them 1:1 for the reason its comment gives: every other weighting is a constant
 * somebody made up.
 *
 * `downloadCount` is no longer part of it. Downloads are the OWNER's own exports of their
 * own track — a private act, and one nobody else's listening contributes to — so ranking a
 * public chart by them made "trending" mean "the creator downloaded this a lot".
 *
 * `seq` breaks the tie, which is what every song in this build hits: with no aggregate
 * data the scores are 0 or 1, so the chart is newest-first, and that is the honest result
 * rather than an invented ordering. It is also the recency preference a real momentum
 * ranking needs — but recency as a TIE-BREAK, not as a decay factor with a half-life
 * nobody has measured.
 */
function byEngagementDesc(
  saves: SaveCounts,
  trendingSignals?: Readonly<Record<string, FeaturedSignalCounts>>
) {
  const scoreFor = (song: Song): number =>
    trendingSignals?.[song.id]
      ? trendingScoreFromCounts(trendingSignals[song.id])
      : trendingScore(song, saves);
  return (a: Song, b: Song): number => scoreFor(b) - scoreFor(a) || bySeqDesc(a, b);
}

/**
 * The ranking behind Featured: the weighted engagement score, newest first on a tie.
 *
 * A DIFFERENT SCORE FROM TRENDING, ON PURPOSE, and this is the one place to say why the two
 * sections are not the same list sorted twice. Trending claims to describe momentum, so it
 * adds its two signals 1:1 — any other weighting there would be invented constants dressed
 * as a model (see engagement.trendingScore). Featured claims only to be a shortlist of the
 * catalogue's strongest tracks, in a stated order of preference: a Love counts for more than
 * a play, a play for more than a save. That ordering is a curation choice, which a curation
 * rule is allowed to be, and engagement.FEATURED_WEIGHTS is it written down.
 *
 * `seq` breaks the tie — recency as a TIE-BREAK, not as a decay factor with a half-life
 * nobody has measured. It is also the whole ordering when nothing has been engaged with at
 * all, which is every song in this build.
 */
function byFeaturedScoreDesc(
  saves: SaveCounts,
  featuredSignals?: Readonly<Record<string, FeaturedSignalCounts>>
) {
  const scoreFor = (song: Song): number =>
    featuredSignals?.[song.id]
      ? featuredScoreFromCounts(featuredSignals[song.id])
      : featuredScore(song, saves);
  return (a: Song, b: Song): number => scoreFor(b) - scoreFor(a) || bySeqDesc(a, b);
}

/**
 * THE PUBLIC CATALOGUE ELIGIBILITY RULE. The one place it is decided.
 *
 *   status === 'ready'   AND   visibility === 'public'
 *
 * TWO CONDITIONS, BECAUSE THEY ARE TWO DIFFERENT FACTS. `ready` means the generator
 * finished; `public` means the owner chose to publish. Treating the first as the second —
 * which this module used to do, when `visibility` did not exist — publishes every finished
 * track the moment it is made, which is a decision the app has no business making on
 * someone's behalf. A private song can be entirely ready, playable and downloadable by its
 * owner and must still never appear here.
 *
 * `unlisted` is excluded as firmly as `private`. It means "reachable by link", not "public
 * but quieter", so a listing surface must not show it; see api/shareApi for the direct-link
 * half of that value.
 *
 * EVERY VIEW ASKS THIS FUNCTION, and none of them re-implements it. That is what makes the
 * rule auditable: `catalogue`, `featured`, `trending`, `explore` and the genre chips all
 * derive from `publicCatalogue()` below, so there is exactly one condition to read and
 * exactly one to change.
 *
 * AND IT IS NOT A SECURITY BOUNDARY. This runs in the browser, on data the browser already
 * holds. It is what stops a private song being *displayed*; it cannot be what stops one
 * being *sent*. The public query itself has to enforce the same rule server-side — see
 * api/discoveryApi's `client-side-visibility-filter` blocker and docs/api-contract.md §22.
 */
export function isPubliclyDiscoverable(song: Song): boolean {
  return song.status === 'ready' && song.visibility === 'public';
}

/**
 * Every publicly discoverable song, newest first. The one list the other three are views of.
 *
 * Empty until somebody lists a song, and correctly so: everything defaults to `'private'`, and
 * only the Library's "Make discoverable" action sets `'public'`. The Home page renders its
 * empty state and says why, rather than this function relaxing its rule to find something to
 * show.
 */
export function publicCatalogue(songs: readonly Song[]): Song[] {
  return songs.filter(isPubliclyDiscoverable).sort(bySeqDesc);
}

/**
 * The genres the catalogue actually contains, sorted.
 *
 * Derived rather than hardcoded, which is the whole point: a filter chip for a genre no
 * song has is a control that returns an empty grid, and a hardcoded list is guaranteed to
 * grow one the moment the genre vocabulary changes. data/music.ts's GENRES is what the
 * *composer* offers; this is what the catalogue holds, and they are not the same question.
 */
export function catalogueGenres(songs: readonly Song[]): string[] {
  const seen = new Set<string>();
  for (const song of songs) {
    // The same eligibility rule the grid uses, so a chip cannot reveal that a private
    // song exists in a genre nothing public occupies — a filter list is a listing too.
    if (isPubliclyDiscoverable(song) && song.genre) seen.add(song.genre);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/**
 * THE FEATURED SELECTION: the catalogue's strongest few, decided automatically.
 *
 * AUTOMATIC, WITH NO FLAG TO SET. There is no `featured` field on `Song`, no Featured
 * management screen and no editorial table — being featured is a consequence of engagement,
 * recomputed on every read. Which also means nobody can promote a song by hand, and nothing
 * about this selection is stored: it is derived, so it cannot go stale.
 *
 * ELIGIBILITY IS NOT ITS OWN RULE. The list starts from `publicCatalogue()`, so the ONE
 * condition in `isPubliclyDiscoverable` governs Featured exactly as it governs Explore.
 * Nothing here re-checks `status` or `visibility`: a private, unlisted, generating, draft or
 * failed song is absent because the catalogue it is drawn from does not contain it, which is
 * a stronger guarantee than a second matching filter would be.
 *
 * IT IS A SHORTLIST, WHICH IS THE POINT. Every discoverable song is in Explore. Featured is
 * the top of the ranking, capped at FEATURED_MAX — otherwise "featured" would mean nothing
 * more than "published", and the two sections would be the same songs in a different order.
 *
 * RANKED BY `byFeaturedScoreDesc`: Loves, then plays, then saves, newest first on a tie.
 *
 * THE LOW-DATA FALLBACK, and it is the path this build actually takes. When fewer than
 * FEATURED_MIN_ENGAGED songs have any engagement at all, a section built only from the ones
 * that do would be one or two cards presented as a curated selection. So the fallback is the
 * best eligible public songs — the same ranking over the whole catalogue, which puts whatever
 * engagement exists on top and orders the rest newest-first. Note what the fallback does NOT
 * do: no count is written onto a song, nothing is presented as engagement it does not have,
 * and the arithmetic is the same either way. Every score being 0 is the honest reading of a
 * library nobody has listened to yet.
 *
 * `saves` is playlists-per-song, supplied by the caller — see ShowcaseOptions.saves. Absent
 * means no saves are known, and no save contributes to the score.
 */
export function featuredSongs(
  songs: readonly Song[],
  saves: SaveCounts = {},
  limit?: number,
  featuredSignals?: Readonly<Record<string, FeaturedSignalCounts>>
): Song[] {
  const scoreFor = (song: Song): number =>
    featuredSignals?.[song.id]
      ? featuredScoreFromCounts(featuredSignals[song.id])
      : featuredScore(song, saves);
  const ranked = publicCatalogue(songs).sort(byFeaturedScoreDesc(saves, featuredSignals));
  const engaged = ranked.filter((s) => scoreFor(s) > 0);
  const shortlist = engaged.length >= FEATURED_MIN_ENGAGED ? engaged : ranked;
  const list = shortlist.slice(0, FEATURED_MAX);
  return limit === undefined ? list : list.slice(0, limit);
}

/**
 * The one track to put forward, or null.
 *
 * The head of the featured selection, so "featured" means one thing in both places.
 * Returns null for an empty catalogue rather than throwing, because "no songs yet" is a
 * state the Home page has to render and not an error.
 */
export function featuredSong(
  songs: readonly Song[],
  saves: SaveCounts = {},
  featuredSignals?: Readonly<Record<string, FeaturedSignalCounts>>
): Song | null {
  return featuredSongs(songs, saves, 1, featuredSignals)[0] ?? null;
}

/** The ranked Trending list. */
export function trendingSongs(
  songs: readonly Song[],
  savesOrLegacyExclude: SaveCounts | Song | null = {},
  limit?: number,
  trendingSignals?: Readonly<Record<string, FeaturedSignalCounts>>
): Song[] {
  const saves =
    savesOrLegacyExclude && typeof savesOrLegacyExclude === 'object' && 'id' in savesOrLegacyExclude
      ? {}
      : ((savesOrLegacyExclude ?? {}) as SaveCounts);
  const ranked = publicCatalogue(songs).sort(byEngagementDesc(saves, trendingSignals));
  return limit === undefined ? ranked : ranked.slice(0, limit);
}

/**
 * The catalogue under one genre, newest first.
 *
 * ALL_GENRES means no filter. An unknown genre correctly yields nothing — it is not
 * silently treated as "everything", because a chip that quietly showed the whole
 * catalogue would look like a filter that works.
 */
export function exploreSongs(songs: readonly Song[], genre: string = ALL_GENRES): Song[] {
  const catalogue = publicCatalogue(songs);
  return genre === ALL_GENRES ? catalogue : catalogue.filter((s) => s.genre === genre);
}

/**
 * Whether one song matches the discovery search box.
 *
 * The Home page and the full Explore screen both ask the same question of the same catalogue,
 * so the predicate lives here rather than as two inline filters that can quietly drift.
 *
 * Trimmed and case-insensitive, exactly as a visitor expects a music search to be. An empty
 * query matches nothing — the caller decides what a blank box means for its screen.
 */
export function matchesDiscoveryQuery(song: Song, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return false;
  return [song.title, song.genre, song.mood, song.creatorName].some((field) =>
    field?.toLowerCase().includes(needle)
  );
}

/**
 * The catalogue and all three views of it, in one pass, for the one screen that shows them.
 *
 * The lists are returned WHOLE. Trimming is the Home page's job (see the *_VISIBLE
 * constants), so expanding a section is a render decision rather than a second request.
 */
export function buildShowcase(
  songs: readonly Song[],
  {
    genre = ALL_GENRES,
    saves = {},
    featuredSignals,
    weeklyFeatured,
    trendingSignals,
    featuredId,
  }: ShowcaseOptions = {}
): Showcase {
  const catalogue = publicCatalogue(songs);
  const effectiveFeaturedSignals = featuredSignals ?? weeklyFeatured;
  const selection = featuredSongs(catalogue, saves, undefined, effectiveFeaturedSignals);
  const featured = (featuredId ? catalogue.find((song) => song.id === featuredId) ?? null : null) ?? selection[0] ?? null;

  return {
    catalogue,
    genres: catalogueGenres(catalogue),
    genre,
    featured,
    featuredRail: selection.filter((song) => song.id !== featured?.id),
    trending: trendingSongs(catalogue, saves, undefined, trendingSignals),
    explore: exploreSongs(catalogue, genre),
  };
}

/** Matches a lyric heading on its own line: Verse 1 / [Verse 1], Chorus / [Chorus]. */
const HEADING = /^\s*(?:\[[^\]]*\]|Intro|Outro|Verse(?:\s+\d+)?|Chorus(?:\s+\d+)?|Bridge|Pre-?Chorus|Hook|Refrain|Refrain\s*\/\s*Outro Hook|Reprise|Section\s+[A-Z0-9]+)\s*$/i;

/**
 * One line of a song's lyrics, for the glimpse a card shows.
 *
 * A GLIMPSE, and the size of it is the requirement: "show a small lyric excerpt … keep
 * this visually subtle … do not display large blocks of lyrics." So this returns a
 * single line, truncated at a word boundary, and the caller renders it on one row that
 * ellipsises if even that does not fit. The whole sheet has exactly one home, which is
 * the lyrics preview dialog.
 *
 * Structural headings are skipped — "[Verse 1]" is not a lyric — and so are blank
 * lines, which is what makes the first *sung* line the one that comes back. Returns
 * null when the sheet has no usable line at all, so the caller can omit the row rather
 * than draw an empty one.
 */
export function lyricExcerpt(song: Song, maxLength = 72): string | null {
  const line = (song.lyrics || '')
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0 && !HEADING.test(l));
  if (!line) return null;
  if (line.length <= maxLength) return line;

  /*
    Cut on a word boundary so the excerpt does not end mid-word.

    The `line[maxLength] === ' '` check is what stops it throwing away a word it did not
    need to: when the budget lands exactly at the end of a word, `clipped` is already
    whole and cutting back to the previous space would drop that word for nothing. Without
    it, a 18-character budget over "one two three four five" returned "one two three".

    A trailing comma is dropped because the ellipsis is the pause — "tighter,…" reads as
    two marks doing one job. Falls back to a hard cut for a single unbroken token, which
    is the only case with no boundary to find.
  */
  const clipped = line.slice(0, maxLength);
  const lastSpace = clipped.lastIndexOf(' ');
  const onBoundary = line[maxLength] === ' ';
  const kept = onBoundary || lastSpace <= 0 ? clipped : clipped.slice(0, lastSpace);
  return `${kept.replace(/[,;:]$/, '')}…`;
}
